import { getVoucherIcon } from '@/components/features/shared/utils'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { InvoiceData, formatInvoiceType } from '@/domain/invoices/invoice'
import { format } from 'date-fns'
import {
	ChevronDown,
	Clipboard,
	X,
} from 'lucide-react'

interface Props {
	invoices?: InvoiceData[]
	onOpenDocument: ({ documentId, fileName }: { documentId: string, fileName: string }) => void
}

const getDisplayLabel = (invoice: InvoiceData) => {
	return invoice.invoiceNumber ? invoice.invoiceNumber : 'Recibo'
}

const sortedInvoices = (invoices: InvoiceData[]) =>
	[...invoices].sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())

export const DownloadButton = ({ invoices, onOpenDocument }: Props) => {
	const sorted = invoices ? sortedInvoices(invoices) : []
	const primary = sorted[0]

	if (!primary) {
		return (
			<Button
				variant="outline"
				size="sm"
				className="w-fit h-7 px-2"
				style={{ cursor: 'default' }}
				title='Factura no disponible'
			>
				<div className='flex items-center gap-2'>
					<div className='relative'>
						<Clipboard className="h-6 w-6" />
						<X className="absolute top-[1px] right-0 h-3.5 w-3.5 text-red-600" style={{ strokeWidth: 2 }} />
					</div>
					<span className='text-[10px] text-muted-foreground font-medium uppercase tracking-tight'>
						Sin factura
					</span>
				</div>
			</Button>
		)
	}

	if (sorted.length === 1) {
		return (
			<Button
				variant="outline"
				size="sm"
				className="w-fit h-7"
				onClick={(e) => { e.stopPropagation(); onOpenDocument({ documentId: primary.id!, fileName: primary.fileName }) }}
			>
				<div className='flex items-center gap-2'>
					{getVoucherIcon(primary.voucherType, primary.invoiceNumber)}
					<span className='text-[11px] font-medium capitalize tracking-tight'>
						{getDisplayLabel(primary)}
					</span>
				</div>
			</Button>
		)
	}

	const invoiceCount = sorted.length
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="w-fit h-7 gap-2"
				>
					<div className='flex items-center gap-2'>
						{getVoucherIcon(primary.voucherType, primary.invoiceNumber)}
						<span className='text-[11px] font-medium tracking-tight'>
							{`${invoiceCount} Comprobantes`}
						</span>
					</div>
					<ChevronDown className="h-3 w-3" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="min-w-[200px]">
				{sorted.map((invoice) => (
					<DropdownMenuItem
						key={invoice.id}
						onClick={(e) => { e.stopPropagation(); onOpenDocument({ documentId: invoice.id!, fileName: invoice.fileName }) }}
					>
						<div className='flex flex-col gap-0.5'>
							<div className='flex items-center gap-1.5'>
								{getVoucherIcon(invoice.voucherType, invoice.invoiceNumber)}
								<span className='text-xs font-medium'>
									{getDisplayLabel(invoice)}
								</span>
							</div>
							<span className='text-[10px] text-muted-foreground'>
								{formatInvoiceType(invoice.voucherType)} — {format(new Date(invoice.uploadDate), 'dd/MM/yyyy HH:mm') + "hs"}
							</span>
						</div>
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}